import type { TFunction } from 'i18next'
import { Alert } from 'react-native'
import { openKycSupport } from './kycSupport'

/**
 * Why verification stopped, and what the buyer may do about it.
 *
 * A retry rides on `network` alone. This alert is the bucket for a condition
 * we did not model: every outcome we understand has a screen of its own — a
 * rejection banner, the options card, a refusal over the live amount. Reaching
 * this alert means we have nothing better to say, so re-running the identical
 * unmodelled path only walks the buyer back into it. A provider that answered
 * and gave up is therefore terminal, and the union makes offering it a retry
 * unrepresentable rather than merely discouraged.
 */
export type KycUnavailability =
  | { reason: 'network'; onRetry?: () => void }
  | { reason: 'provider' }
  // The same refusal on a rail whose round is still live. Stripe's repair is the
  // one that cannot succeed twice; a provider that mounts its own document
  // surface publishes a fresh attempt, so dismissing returns the buyer to it
  // rather than stranding them on an alert over a surface that still works.
  | { reason: 'providerRetryable'; onDismiss?: () => void }

export function showKycUnavailableAlert(
  t: TFunction,
  unavailability: KycUnavailability,
) {
  if (unavailability.reason === 'network') {
    const { onRetry } = unavailability
    Alert.alert(
      t('fiat.kycUnavailableTitle'),
      t('fiat.kycUnavailableNetwork'),
      [
        {
          text: t('common.ok'),
          ...(onRetry === undefined ? {} : { onPress: onRetry }),
        },
      ],
    )
    return
  }
  if (unavailability.reason === 'providerRetryable') {
    const { onDismiss } = unavailability
    Alert.alert(
      t('fiat.kycUnavailableTitle'),
      t('fiat.kycUnavailableProvider'),
      [
        {
          text: t('common.ok'),
          isPreferred: true,
          ...(onDismiss === undefined ? {} : { onPress: onDismiss }),
        },
        {
          text: t('fiat.contactSupport'),
          onPress: () => {
            openKycSupport()
            onDismiss?.()
          },
        },
      ],
    )
    return
  }
  Alert.alert(
    t('fiat.kycUnavailableProviderTitle'),
    t('fiat.kycUnavailableProvider'),
    [
      { text: t('common.ok'), isPreferred: true },
      { text: t('fiat.contactSupport'), onPress: openKycSupport },
    ],
  )
}

/**
 * A surface that never loaded is a connection problem; only a provider that
 * answered and gave up should send the user to support.
 */
export function surfaceFailureReason(code: string): 'network' | 'provider' {
  if (code.startsWith('load_failed')) {
    return 'network'
  }
  return 'provider'
}
